import React from 'react';
import PropTypes from 'prop-types';
import ProductCard from './ProductCard';

/**
 * SearchResults Component
 * Displays a grid of search results with loading and empty states
 */
const SearchResults = ({ results, loading, onProductClick }) => {
  // Styles
  const styles = {
    container: {
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 20px',
    },
    header: {
      marginBottom: '32px',
      textAlign: 'center',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '8px',
    },
    subtitle: {
      fontSize: '16px',
      color: '#6b7280',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 20px',
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    loadingText: {
      marginTop: '20px',
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500',
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 20px',
      backgroundColor: '#f9fafb',
      borderRadius: '12px',
      border: '2px dashed #d1d5db',
    },
    emptyIcon: {
      fontSize: '64px',
      marginBottom: '16px',
    },
    emptyTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#4b5563',
      marginBottom: '8px',
    },
    emptyText: {
      fontSize: '16px',
      color: '#6b7280',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '40px',
    },
  };

  // Add keyframes for spinner animation
  React.useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    try {
      styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    } catch (e) {
      // Rule might already exist
    }
  }, []);

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <div style={styles.loadingText}>Searching for similar jewelry...</div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!results || results.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🔍</div>
          <div style={styles.emptyTitle}>No results yet</div>
          <div style={styles.emptyText}>
            Upload an image to find similar jewelry items
          </div>
        </div>
      </div>
    );
  }

  // Results state - limit to 8 items
  const displayedResults = results.slice(0, 8);

  return (
    <div style={styles.container}>
      {/* Responsive Grid */}
      <div style={styles.grid}>
        {displayedResults.map((result, index) => (
          <ProductCard
            key={result.sku || index}
            rank={index + 1}
            sku={result.sku}
            confidence={result.confidence}
            category={result.category}
            metadata={result.metadata}
            imageUrl={result.imageUrl}
            onClick={() => onProductClick && onProductClick(result)}
          />
        ))}
      </div>
    </div>
  );
};

// PropTypes validation
SearchResults.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string.isRequired,
      confidence: PropTypes.number.isRequired,
      category: PropTypes.string,
      metadata: PropTypes.object,
      imageUrl: PropTypes.string,
    })
  ),
  loading: PropTypes.bool.isRequired,
  onProductClick: PropTypes.func,
};

SearchResults.defaultProps = {
  results: [],
  onProductClick: null,
};

export default SearchResults;
